import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Newport Beach", cities: "Balboa Island, Corona del Mar, Newport Coast" },
    { zone: "Costa Mesa", cities: "Costa Mesa, Fashion Island, Eastside Costa Mesa" },
    { zone: "Laguna Beach", cities: "Laguna Beach, Laguna Hills, Laguna Niguel" },
    { zone: "Irvine", cities: "Irvine, Tustin, Lake Forest" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Newport Beach</h2>
          <p className="text-muted-foreground">Limpieza de lujo para hogares en Newport Beach, Balboa Island, Corona del Mar, Costa Mesa y áreas circundantes.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar en Newport Beach</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const NewportBeachSpanishPage = () => (
  <LocationContext.Provider value={{ slug: "newport-beach", name: "Newport Beach", type: "city", regionSlug: "orange-county" }}>
    <SEO
      title="Limpieza del Hogar en Newport Beach, CA — Uplevel Cleaning"
      description="Servicio premium de limpieza del hogar en Newport Beach, CA. Precios planos, reserva en 60 segundos, garantía del 200%. Especialistas en propiedades de lujo y frente al mar."
      keywords="limpieza newport beach, maid service newport beach español, servicio de limpieza newport beach ca, empleada doméstica newport beach, limpieza balboa island"
      url="https://uplevelcleaning.com/es/newport-beach"
      locationName="Newport Beach"
      regionSlug="orange-county"
      lang="es"
      includeLocalBusiness={true}
      includeFAQ={true}
      breadcrumbs={[{ name: "Inicio", url: "/" }, { name: "Orange County", url: "/es/orange-county" }, { name: "Newport Beach", url: "/es/newport-beach" }]}
      reviews={[
        { author: "Alejandra V.", rating: 5, reviewBody: "Tenemos una casa frente al mar en Newport Beach y Uplevel la mantiene perfecta. Son profesionales, confiables y muy detallistas. 100% recomendados.", datePublished: "2026-03-14" },
        { author: "Carlos E.", rating: 5, reviewBody: "El equipo llegó puntual, fue amable y el resultado fue impecable. Para una propiedad de lujo como la nuestra, confiar en el servicio es esencial. Uplevel cumplió.", datePublished: "2026-02-20" },
      ]}
    />
    <div className="flex min-h-screen flex-col" lang="es">
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <SpanishHero cityName="Newport Beach" regionSlug="orange-county" />
        <div className="bg-primary/5 border-b border-primary/10 py-4">
          <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
            {["Servicio Local en Newport Beach", "Especialistas en Propiedades de Lujo", "Garantía del 200%", "Reserva en 60 Segundos"].map((item, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {item}</span>
            ))}
          </div>
        </div>
        <SpanishWhyUs cityName="Newport Beach" />
        <NearbyAreas />
        <SpanishServices />

        <FrequencyDiscounts />
        <PricingCalculator />
        <SpanishTestimonials region="orange-county" />
        <Guarantee />
        <SpanishFAQ cityName="Newport Beach" />
        <InternalLinks />
        <SpanishFinalCTA cityName="Newport Beach" />
      </main>
      <Footer />
    </div>
  </LocationContext.Provider>
);

export default NewportBeachSpanishPage;
